import { useFormContext } from 'react-hook-form';
import {
  getDefaultElectronicOperator,
  getDeliveryMethodDropDownOptions,
} from '../../../../common/utils/billingAccountUtils.js';
import { BillingAccountDeliveryMethod } from '../../../../generated/api/models.js';
import { BillingAccountElectronicDeliveryMethodEdit } from './BillingAccountElectronicDeliveryMethodEdit.js';
import { BillingAccountEmailDeliveryMethodEdit } from './BillingAccountEmailDeliveryMethodEdit.js';
import { BillingAccountPaperDeliveryMethodEdit } from './BillingAccountPaperDeliveryMethodEdit.js';
import type { BillChannel, BillingAccount, Contact } from '../../../../generated/api/models.js';

export interface BillingAccountDeliveryMethodEditProps {
  billingAccount: BillingAccount;
  billChannels: BillChannel[];
  disabled?: boolean;
  selectedDeliveryMethod: BillingAccountDeliveryMethod;
  contacts: Contact[];
  onCreateContact?: () => void;
}

export const BillingAccountDeliveryMethodEditForm = ({
  billingAccount,
  billChannels,
  disabled = false,
  selectedDeliveryMethod,
  contacts,
  onCreateContact,
}: BillingAccountDeliveryMethodEditProps) => {
  const deliveryMethodOptions = getDeliveryMethodDropDownOptions(billingAccount, billChannels);
  const { setValue } = useFormContext();

  const handleDeliveryMethodChange = (newMethod: BillingAccountDeliveryMethod) => {
    if (newMethod === BillingAccountDeliveryMethod.ELECTRONIC) {
      const defaultOperator = getDefaultElectronicOperator(billChannels);
      const defaultElectronicAddress =
        defaultOperator?.active && defaultOperator?.electronicAddress ? defaultOperator.electronicAddress : '';

      setValue('billingAccount.billElectronicOperator', defaultOperator?.id);
      setValue('billingAccount.billElectronicAddress', defaultElectronicAddress);
    }
  };

  switch (selectedDeliveryMethod) {
    case BillingAccountDeliveryMethod.ELECTRONIC:
      return (
        <BillingAccountElectronicDeliveryMethodEdit
          billChannels={billChannels}
          deliveryMethodOptions={deliveryMethodOptions}
          disabled={disabled}
        />
      );
    case BillingAccountDeliveryMethod.EMAIL:
      return (
        <BillingAccountEmailDeliveryMethodEdit
          deliveryMethodOptions={deliveryMethodOptions}
          disabled={disabled}
          contacts={contacts}
          onDeliveryMethodChange={handleDeliveryMethodChange}
          onCreateContact={onCreateContact}
        />
      );
    case BillingAccountDeliveryMethod.PAPER:
      return (
        <BillingAccountPaperDeliveryMethodEdit
          deliveryMethodOptions={deliveryMethodOptions}
          disabled={disabled}
          onDeliveryMethodChange={handleDeliveryMethodChange}
        />
      );
    default:
      return null;
  }
};
